import axios from "axios";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API_URL from "../../common/config2"; // Adjust the import based on your project structure

export default function Step1BasicInfo({
  formData,
  setFormData,
  errors,
  setErrors,
  isLoading,
  onSubmit,
  validateBasicInfo,
  onGoogleSignup,
  csrfToken, // CSRF token for secure requests
}) {
  // Password validation state

  const [passwordStrength, setPasswordStrength] = useState({
    hasLength: false,
    hasUppercase: false,
    hasNumber: false,
  });

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Real-time password strength indicator
    if (name === "password") {
      setPasswordStrength({
        hasLength: value.length >= 8,
        hasUppercase: /[A-Z]/.test(value),
        hasNumber: /[0-9]/.test(value),
      });
    }
  };

  // Handle form submission with validation
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Optional: Set loading state here
      // setIsLoading(true);

      const otpResponse = await axios.post(
        `${API_URL}/auth-check`,
        { email: formData.email },
        {
          headers: {
            "x-csrf-token": csrfToken,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      const userExists = otpResponse?.data?.exists;

      if (!userExists) {
        if (validateBasicInfo()) {
          onSubmit(); // Call your form submit logic
        } else {
          // Scroll to the first validation error
          const firstErrorElement = document.querySelector(".error-msg");
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      } else {
        alert("This email is already registered. Please log in.");
        window.location.href = "/login";
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Something went wrong. Please try again later.");
    } finally {
      // Optional: Clear loading state here
      // setIsLoading(false);
    }
  };

  return (
    <>
      <div className="sec-heading centered">
        <div className="content-area">
          <h2
            className="title wow fadeInUp"
            style={{ fontSize: "1.75rem" }}
            data-wow-delay=".4s">
            Join UIM — Create Your Account!
          </h2>
          <p className="subtitle-text wow fadeInUp" data-wow-delay=".6s">
            Already a Member?{" "}
            <Link to="/login" className="link" style={{ color: "#09aff4" }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>

      <div className="contact-form-inputs wow fadeInUp" data-wow-delay=".6s">
        <div
          className="custom-form-area input-boxed"
          style={{ padding: "0px" }}>
          <button
            type="button"
            onClick={onGoogleSignup}
            disabled={isLoading}
            className="btn-outline w-100 d-flex align-items-center justify-content-center mb-4 btn-solid"
            aria-label="Sign up with Google">
            <div className="me-2">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden="true">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            </div>
            {isLoading && formData.googleSignIn ? (
              <div className="d-flex align-items-center">
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"></span>
                <span>Processing...</span>
              </div>
            ) : (
              <span>Sign Up with Google</span>
            )}
          </button>

          <div className="d-flex align-items-center mb-4">
            <hr className="flex-grow-1" />
            <span className="px-3 small">or</span>
            <hr className="flex-grow-1" />
          </div>

          <form className="main-form" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <div className="input-wrapper">
                  <input
                    className={`text-input ${
                      errors.firstName ? "is-invalid" : ""
                    }`}
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    aria-label="First name"
                    aria-invalid={errors.firstName ? "true" : "false"}
                    style={{
                      borderColor: errors.firstName ? "#dc3545" : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                  />
                  <label className="input-label" htmlFor="firstName">
                    First Name{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors.firstName && (
                    <span
                      className="error-msg"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.firstName}
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="input-wrapper">
                  <input
                    className={`text-input ${
                      errors.lastName ? "is-invalid" : ""
                    }`}
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    aria-label="Last name"
                    aria-invalid={errors.lastName ? "true" : "false"}
                    style={{
                      borderColor: errors.lastName ? "#dc3545" : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                  />
                  <label className="input-label" htmlFor="lastName">
                    Last Name{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors.lastName && (
                    <span
                      className="error-msg"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.lastName}
                    </span>
                  )}
                </div>
              </div>

              <div className="col-6">
                <div className="input-wrapper">
                  <input
                    className={`text-input ${errors.email ? "is-invalid" : ""}`}
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    aria-label="Email address"
                    aria-invalid={errors.email ? "true" : "false"}
                    style={{
                      borderColor: errors.email ? "#dc3545" : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                    required
                  />
                  <label className="input-label" htmlFor="email">
                    Email Address{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors.email && (
                    <span
                      className="error-msg"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.email}
                    </span>
                  )}
                </div>
              </div>

              <div className="col-6">
                <div className="input-wrapper">
                  <input
                    className={`text-input ${
                      errors.password ? "is-invalid" : ""
                    }`}
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    aria-label="Password"
                    aria-invalid={errors.password ? "true" : "false"}
                    style={{
                      borderColor: errors.password ? "#dc3545" : "#ced4da",
                      padding: "12px",
                      borderRadius: "4px",
                    }}
                  />
                  <label className="input-label" htmlFor="password">
                    Password{" "}
                    <span className="req" style={{ color: "#dc3545" }}>
                      *
                    </span>
                  </label>
                  <span className="b-border" />
                  {errors.password && (
                    <span
                      className="error-msg"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.password}
                    </span>
                  )}

                  {/* Password strength indicator */}
                  {formData.password && (
                    <div className="password-strength mt-2">
                      <div className="requirements small">
                        <div
                          className={
                            passwordStrength.hasLength
                              ? "text-success"
                              : "text-muted"
                          }>
                          <i
                            className={`fas fa-${
                              passwordStrength.hasLength ? "check" : "times"
                            } me-1`}></i>
                          At least 8 characters
                        </div>
                        <div
                          className={
                            passwordStrength.hasUppercase
                              ? "text-success"
                              : "text-muted"
                          }>
                          <i
                            className={`fas fa-${
                              passwordStrength.hasUppercase ? "check" : "times"
                            } me-1`}></i>
                          At least 1 uppercase letter
                        </div>
                        <div
                          className={
                            passwordStrength.hasNumber
                              ? "text-success"
                              : "text-muted"
                          }>
                          <i
                            className={`fas fa-${
                              passwordStrength.hasNumber ? "check" : "times"
                            } me-1`}></i>
                          At least 1 number
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 ">
                <div className="form-check">
                  <input
                    className={`form-check-input ${
                      errors.agreeToTerms ? "is-invalid" : ""
                    }`}
                    type="checkbox"
                    id="agreeToTerms"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    aria-label="Agree to terms and services"
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="agreeToTerms"
                    style={{ cursor: "pointer" }}>
                    I agree to the{" "}
                    <a href="#" className="link" style={{ color: "#09aff4" }}>
                      Terms of services
                    </a>{" "}
                    and{" "}
                    <a href="#" className="link" style={{ color: "#09aff4" }}>
                      privacy policy
                    </a>
                  </label>
                  {errors.agreeToTerms && (
                    <div
                      className="error-msg mt-1 small"
                      role="alert"
                      style={{ color: "#dc3545", fontSize: "0.875rem" }}>
                      {errors.agreeToTerms}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 submit-wrapper">
                <button
                  type="submit"
                  className="btn-solid w-100"
                  disabled={isLoading}
                  aria-label="Sign up">
                  {isLoading && !formData.googleSignIn ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    "Sign Up"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
